/** @module @liquidefy/code-editor/ai/ai-analysis @version 1.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS ═══

import type {
  CodeAnalysisType,
  CodeAnalysisRequest,
  CodeAnalysisResult,
  AnalysisTarget,
  AnalysisOptions,
  AnalysisSummary,
  AnalysisFinding,
  ComplexityMetrics,
  HalsteadMetrics,
  MaintainabilityMetrics,
  DuplicateBlock,
  DuplicationMetrics,
  CodeMetrics,
  Range,
  EditorResult,
} from '../../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  now,
} from '../../code-editor.js';

// ═══ §2 ANALYSIS REQUESTS — build typed analysis request objects ═══

/**
 * Build a code analysis request with type, target, and options.
 */
export function buildAnalysisRequest(
  type: CodeAnalysisType,
  target: AnalysisTarget,
  options?: Partial<AnalysisOptions>,
): CodeAnalysisRequest {
  return {
    type,
    target,
    options: {
      depth: options?.depth ?? 'standard',
      includeHistory: options?.includeHistory ?? false,
      includeDependencies: options?.includeDependencies ?? false,
      timeout: options?.timeout ?? 30000,
    },
  };
}

/**
 * Quick (shallow) analysis.
 */
export function quickAnalysis(target: AnalysisTarget): CodeAnalysisRequest {
  return buildAnalysisRequest('quality', target, { depth: 'shallow', timeout: 5000 });
}

/**
 * Deep analysis with full dependency and history tracking.
 */
export function deepAnalysis(target: AnalysisTarget): CodeAnalysisRequest {
  return buildAnalysisRequest('quality', target, { depth: 'deep', includeHistory: true, includeDependencies: true, timeout: 60000 });
}

/**
 * Security-focused analysis.
 */
export function securityAnalysis(target: AnalysisTarget): CodeAnalysisRequest {
  return buildAnalysisRequest('security', target, { depth: 'deep', timeout: 45000 });
}

/**
 * Performance-focused analysis.
 */
export function performanceAnalysis(target: AnalysisTarget): CodeAnalysisRequest {
  return buildAnalysisRequest('performance', target, { depth: 'standard', timeout: 30000 });
}

/**
 * Architecture analysis.
 */
export function architectureAnalysis(target: AnalysisTarget): CodeAnalysisRequest {
  return buildAnalysisRequest('architecture', target, { depth: 'deep', includeDependencies: true, timeout: 60000 });
}

/**
 * Dependency analysis.
 */
export function dependencyAnalysis(target: AnalysisTarget): CodeAnalysisRequest {
  return buildAnalysisRequest('dependencies', target, { depth: 'deep', includeDependencies: true });
}

/**
 * Overall quality analysis.
 */
export function qualityAnalysis(target: AnalysisTarget): CodeAnalysisRequest {
  return buildAnalysisRequest('quality', target, { depth: 'standard' });
}

// ═══ §3 COMPLEXITY METRICS — cyclomatic, cognitive, Halstead ═══

/**
 * Complexity thresholds.
 */
export const COMPLEXITY_THRESHOLDS = {
  low: 5,
  medium: 10,
  high: 20,
  veryHigh: 50,
} as const;

/**
 * Compute cyclomatic complexity by counting decision points.
 */
export function computeCyclomatic(code: string): number {
  let complexity = 1; // Base path

  const decisionPatterns = [
    /\bif\b/g,
    /\belse\s+if\b/g,
    /\bcase\b/g,
    /\bwhile\b/g,
    /\bfor\b/g,
    /\bdo\b(?=\s*\{)/g,
    /\bcatch\b/g,
    /\?\?/g,
    /\?\./g,
    /&&/g,
    /\|\|/g,
    /\?(?!=)/g,
  ];

  for (const pattern of decisionPatterns) {
    const matches = code.match(pattern);
    if (matches) complexity += matches.length;
  }

  return complexity;
}

/**
 * Compute cognitive complexity — weighted by nesting depth and branching.
 */
export function computeCognitive(code: string): number {
  const lines = code.split('\n');
  let complexity = 0;
  let nestingLevel = 0;

  for (const line of lines) {
    const trimmed = line.trim();

    // Track nesting
    const opens = (trimmed.match(/\{/g) ?? []).length;
    const closes = (trimmed.match(/\}/g) ?? []).length;

    // Decision points add 1 + nesting increment
    if (/\bif\b/.test(trimmed)) complexity += 1 + nestingLevel;
    if (/\belse\s+if\b/.test(trimmed)) complexity += 1 + nestingLevel;
    if (/\belse\b/.test(trimmed) && !/\belse\s+if\b/.test(trimmed)) complexity += 1;
    if (/\bfor\b/.test(trimmed)) complexity += 1 + nestingLevel;
    if (/\bwhile\b/.test(trimmed)) complexity += 1 + nestingLevel;
    if (/\bswitch\b/.test(trimmed)) complexity += 1 + nestingLevel;
    if (/\bcatch\b/.test(trimmed)) complexity += 1 + nestingLevel;

    // Ternary operators
    const ternaries = (trimmed.match(/\?(?!=)/g) ?? []).length;
    complexity += ternaries * (1 + nestingLevel);

    // Logical operators (sequence breaks)
    const logicals = (trimmed.match(/&&|\|\|/g) ?? []).length;
    complexity += logicals;

    // Recursion (calling the containing function) — simplified check
    if (/\brecur(?:se|sion)?\b/.test(trimmed)) complexity += 1;

    nestingLevel += opens - closes;
    nestingLevel = Math.max(0, nestingLevel);
  }

  return complexity;
}

/**
 * Compute Halstead metrics from operator/operand analysis.
 */
export function computeHalstead(code: string): HalsteadMetrics {
  // Extract operators
  const operatorPatterns = /[+\-*/%=<>!&|^~?:;,.(){}[\]]/g;
  const keywordPatterns = /\b(?:if|else|for|while|do|switch|case|break|continue|return|throw|try|catch|finally|new|delete|typeof|instanceof|in|of|class|function|const|let|var|import|export|default|async|await|yield)\b/g;

  const operators = new Set<string>();
  const operands = new Set<string>();
  let totalOperators = 0;
  let totalOperands = 0;

  // Count operators
  const opMatches = code.match(operatorPatterns) ?? [];
  for (const op of opMatches) {
    operators.add(op);
    totalOperators++;
  }
  const kwMatches = code.match(keywordPatterns) ?? [];
  for (const kw of kwMatches) {
    operators.add(kw);
    totalOperators++;
  }

  // Count operands (identifiers and literals)
  const identPattern = /\b[a-zA-Z_]\w*\b/g;
  const identMatches = code.match(identPattern) ?? [];
  const keywords = new Set(kwMatches);
  for (const ident of identMatches) {
    if (!keywords.has(ident)) {
      operands.add(ident);
      totalOperands++;
    }
  }

  // Number literals
  const numMatches = code.match(/\b\d+(?:\.\d+)?\b/g) ?? [];
  for (const num of numMatches) {
    operands.add(num);
    totalOperands++;
  }

  // String literals
  const strMatches = code.match(/(['"`])(?:(?!\1|\\).|\\.)*\1/g) ?? [];
  for (const str of strMatches) {
    operands.add(str);
    totalOperands++;
  }

  const n1 = operators.size || 1;
  const n2 = operands.size || 1;
  const N1 = totalOperators || 1;
  const N2 = totalOperands || 1;

  const length = N1 + N2;
  const vocabulary = n1 + n2;
  const volume = length * Math.log2(vocabulary || 2);
  const difficulty = (n1 / 2) * (N2 / n2);
  const effort = difficulty * volume;
  const timeToImplement = effort / 18;
  const estimatedBugs = volume / 3000;

  return {
    length,
    vocabulary,
    volume: Math.round(volume * 100) / 100,
    difficulty: Math.round(difficulty * 100) / 100,
    effort: Math.round(effort * 100) / 100,
    timeToImplement: Math.round(timeToImplement * 100) / 100,
    estimatedBugs: Math.round(estimatedBugs * 1000) / 1000,
  };
}

/**
 * Compute maximum nesting depth.
 */
export function computeNestingDepth(code: string): number {
  let maxDepth = 0;
  let depth = 0;
  for (const ch of code) {
    if (ch === '{') { depth++; maxDepth = Math.max(maxDepth, depth); }
    if (ch === '}') depth = Math.max(0, depth - 1);
  }
  return maxDepth;
}

/**
 * Compute complexity for a specific function within the code.
 */
export function functionComplexity(code: string, functionName: string): number {
  const escapedName = functionName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const pattern = new RegExp(`(?:function\\s+${escapedName}|(?:const|let)\\s+${escapedName}\\s*=)\\s*(?:async\\s*)?\\([^)]*\\)\\s*(?:=>\\s*)?\\{`, 'g');
  const match = pattern.exec(code);
  if (!match) return 0;

  const start = match.index + match[0].length;
  let depth = 1;
  let end = start;
  while (end < code.length && depth > 0) {
    if (code[end] === '{') depth++;
    if (code[end] === '}') depth--;
    end++;
  }

  const body = code.slice(match.index, end);
  return computeCyclomatic(body);
}

/**
 * Compute aggregate file complexity.
 */
export function fileComplexity(code: string): ComplexityMetrics {
  return {
    cyclomatic: computeCyclomatic(code),
    cognitive: computeCognitive(code),
    halstead: computeHalstead(code),
  };
}

// ═══ §4 MAINTAINABILITY — index, ratios, function metrics ═══

/**
 * Compute the Maintainability Index (MI).
 * MI = MAX(0, (171 - 5.2 * ln(V) - 0.23 * G - 16.2 * ln(LOC)) * 100 / 171)
 */
export function computeMaintainabilityIndex(
  halstead: HalsteadMetrics,
  cyclomatic: number,
  loc: number,
): number {
  const volume = halstead.volume || 1;
  const linesOfCode = loc || 1;
  const raw = 171 - 5.2 * Math.log(volume) - 0.23 * cyclomatic - 16.2 * Math.log(linesOfCode);
  return Math.max(0, Math.min(100, (raw * 100) / 171));
}

/**
 * Compute ratio of comment lines to total lines.
 */
export function commentRatio(code: string): number {
  const lines = code.split('\n');
  if (lines.length === 0) return 0;
  const commentLines = lines.filter((l) => {
    const trimmed = l.trim();
    return trimmed.startsWith('//') || trimmed.startsWith('/*') || trimmed.startsWith('*') || trimmed.startsWith('#');
  }).length;
  return commentLines / lines.length;
}

/**
 * Compute code-to-comment ratio (code lines / comment lines).
 */
export function codeToCommentRatio(code: string): number {
  const lines = code.split('\n');
  let codeLines = 0;
  let comments = 0;
  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.length === 0) continue;
    if (trimmed.startsWith('//') || trimmed.startsWith('/*') || trimmed.startsWith('*') || trimmed.startsWith('#')) {
      comments++;
    } else {
      codeLines++;
    }
  }
  return comments > 0 ? codeLines / comments : codeLines;
}

/**
 * Average function length in lines.
 */
export function averageFunctionLength(code: string): number {
  const lengths = functionLengths(code);
  if (lengths.length === 0) return 0;
  return lengths.reduce((sum, l) => sum + l, 0) / lengths.length;
}

/**
 * Length of the longest function in lines.
 */
export function longestFunction(code: string): number {
  const lengths = functionLengths(code);
  return lengths.length > 0 ? Math.max(...lengths) : 0;
}

/**
 * Estimate documentation coverage (percentage of exported symbols that have doc comments).
 */
export function documentationCoverage(code: string): number {
  const exportPattern = /export\s+(?:function|const|class|interface|type|enum)\s+(\w+)/g;
  let totalExports = 0;
  let documented = 0;
  let match: RegExpExecArray | null;
  while ((match = exportPattern.exec(code)) !== null) {
    totalExports++;
    // Check if preceded by JSDoc comment
    const before = code.slice(Math.max(0, match.index - 200), match.index);
    if (/\/\*\*[\s\S]*?\*\/\s*$/.test(before)) {
      documented++;
    }
  }
  return totalExports > 0 ? documented / totalExports : 1;
}

/**
 * Rough estimate of test coverage: how many exported functions have a matching test.
 */
export function testCoverage(code: string, tests: string): number {
  const exportPattern = /export\s+(?:function|const)\s+(\w+)/g;
  const symbols: string[] = [];
  let match: RegExpExecArray | null;
  while ((match = exportPattern.exec(code)) !== null) {
    symbols.push(match[1]!);
  }
  if (symbols.length === 0) return 1;

  let covered = 0;
  for (const sym of symbols) {
    if (tests.includes(sym)) covered++;
  }
  return covered / symbols.length;
}

function functionLengths(code: string): number[] {
  const lengths: number[] = [];
  const funcPattern = /(?:function\s+\w+|(?:const|let)\s+\w+\s*=\s*(?:async\s*)?\([^)]*\)\s*=>)\s*\{/g;
  let match: RegExpExecArray | null;
  while ((match = funcPattern.exec(code)) !== null) {
    const start = match.index + match[0].length;
    let depth = 1;
    let end = start;
    while (end < code.length && depth > 0) {
      if (code[end] === '{') depth++;
      if (code[end] === '}') depth--;
      end++;
    }
    const body = code.slice(start, end - 1);
    lengths.push(body.split('\n').length);
  }
  return lengths;
}

// ═══ §5 DUPLICATION — detect duplicate code blocks ═══

/**
 * Find duplicate code blocks in the source.
 */
export function findDuplicates(code: string, minLines: number = 3, minTokens: number = 25): DuplicateBlock[] {
  const lines = code.split('\n');
  const duplicates: DuplicateBlock[] = [];
  const seen = new Map<string, number[]>();

  for (let i = 0; i <= lines.length - minLines; i++) {
    const block = lines.slice(i, i + minLines);
    const normalized = normalizeForComparison(block.join('\n'));
    if (normalized.split(/\s+/).length < minTokens) continue;

    const hash = hashBlock(normalized, 0, 0);
    const existing = seen.get(hash);
    if (existing) {
      existing.push(i);
    } else {
      seen.set(hash, [i]);
    }
  }

  for (const [, lineNumbers] of seen) {
    if (lineNumbers.length < 2) continue;
    const ranges: Range[] = lineNumbers.map((ln) => ({
      start: { line: ln, column: 0 },
      end: { line: ln + minLines - 1, column: 0 },
    }));
    const tokenCount = normalizeForComparison(lines.slice(lineNumbers[0]!, lineNumbers[0]! + minLines).join('\n')).split(/\s+/).length;
    duplicates.push({
      ranges,
      lineCount: minLines,
      tokenCount,
    });
  }

  return duplicates;
}

/**
 * Compute duplication percentage.
 */
export function computeDuplicationPercentage(code: string, duplicates: readonly DuplicateBlock[]): number {
  const totalLines = code.split('\n').length;
  if (totalLines === 0) return 0;

  const duplicatedLines = new Set<number>();
  for (const block of duplicates) {
    for (const range of block.ranges) {
      for (let i = range.start.line; i <= range.end.line; i++) {
        duplicatedLines.add(i);
      }
    }
  }

  return duplicatedLines.size / totalLines;
}

/**
 * Suggest extraction for a duplicate block.
 */
export function suggestExtraction(block: DuplicateBlock): string {
  return `Extract ${block.lineCount}-line block (found ${block.ranges.length} copies) into a shared function or constant.`;
}

/**
 * Hash a code block for comparison.
 */
export function hashBlock(code: string, _startLine: number, _endLine: number): string {
  const normalized = normalizeForComparison(code);
  let hash = 0;
  for (let i = 0; i < normalized.length; i++) {
    const ch = normalized.charCodeAt(i);
    hash = ((hash << 5) - hash + ch) | 0;
  }
  return Math.abs(hash).toString(36);
}

/**
 * Normalize code for comparison by stripping whitespace and comments.
 */
export function normalizeForComparison(code: string): string {
  return code
    .replace(/\/\/.*$/gm, '')           // Remove single-line comments
    .replace(/\/\*[\s\S]*?\*\//g, '')   // Remove multi-line comments
    .replace(/\s+/g, ' ')               // Collapse whitespace
    .trim();
}

// ═══ §6 FINDINGS — create, categorize, prioritize ═══

/**
 * Create an analysis finding.
 */
export function createFinding(
  type: string,
  severity: AnalysisFinding['severity'],
  title: string,
  description: string,
  location: Range,
  evidence?: string,
): AnalysisFinding {
  return {
    id: generateId('find'),
    type,
    severity,
    title,
    description,
    location,
    evidence: evidence ?? '',
  };
}

/**
 * Categorize findings by type.
 */
export function categorizeFindings(findings: readonly AnalysisFinding[]): Record<string, AnalysisFinding[]> {
  const categories: Record<string, AnalysisFinding[]> = {};
  for (const finding of findings) {
    if (!categories[finding.type]) categories[finding.type] = [];
    categories[finding.type]!.push(finding);
  }
  return categories;
}

/**
 * Prioritize findings: critical first, then high, medium, low, info.
 */
export function prioritizeFindings(findings: readonly AnalysisFinding[]): AnalysisFinding[] {
  const severityOrder: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };
  return [...findings].sort((a, b) => (severityOrder[a.severity] ?? 5) - (severityOrder[b.severity] ?? 5));
}

/**
 * Filter findings by minimum severity.
 */
export function filterBySeverity(
  findings: readonly AnalysisFinding[],
  minSeverity: AnalysisFinding['severity'],
): AnalysisFinding[] {
  const severityOrder: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };
  const minOrder = severityOrder[minSeverity] ?? 5;
  return findings.filter((f) => (severityOrder[f.severity] ?? 5) <= minOrder);
}

/**
 * Group findings by file (using location start line as proxy).
 */
export function groupByFile(findings: readonly AnalysisFinding[]): Record<string, AnalysisFinding[]> {
  const groups: Record<string, AnalysisFinding[]> = {};
  for (const finding of findings) {
    const key = `line-${finding.location.start.line}`;
    if (!groups[key]) groups[key] = [];
    groups[key].push(finding);
  }
  return groups;
}

/**
 * Total count of findings.
 */
export function findingsCount(findings: readonly AnalysisFinding[]): number {
  return findings.length;
}

/**
 * Count of critical-severity findings.
 */
export function criticalCount(findings: readonly AnalysisFinding[]): number {
  return findings.filter((f) => f.severity === 'critical').length;
}

// ═══ §7 HEALTH SCORING — aggregate metrics into scores, grades, reports ═══

/**
 * Health scoring weights.
 */
export const HEALTH_WEIGHTS = {
  complexity: 0.2,
  maintainability: 0.3,
  duplication: 0.15,
  security: 0.2,
  style: 0.15,
} as const;

/**
 * Compute a health score (0-100) from findings and metrics.
 */
export function computeHealthScore(
  findings: readonly AnalysisFinding[],
  metrics: CodeMetrics,
): number {
  // Start at 100, deduct based on issues
  let score = 100;

  // Deduct for findings by severity
  const criticals = findings.filter((f) => f.severity === 'critical').length;
  const highs = findings.filter((f) => f.severity === 'high').length;
  const mediums = findings.filter((f) => f.severity === 'medium').length;
  const lows = findings.filter((f) => f.severity === 'low').length;

  score -= criticals * 15;
  score -= highs * 8;
  score -= mediums * 3;
  score -= lows * 1;

  // Deduct for complexity
  if (metrics.complexity.cyclomatic > COMPLEXITY_THRESHOLDS.high) score -= 10;
  else if (metrics.complexity.cyclomatic > COMPLEXITY_THRESHOLDS.medium) score -= 5;

  // Deduct for low maintainability
  if (metrics.maintainability.index < 40) score -= 15;
  else if (metrics.maintainability.index < 65) score -= 8;

  // Deduct for duplication
  if (metrics.duplication.percentage > 0.3) score -= 15;
  else if (metrics.duplication.percentage > 0.15) score -= 8;
  else if (metrics.duplication.percentage > 0.05) score -= 3;

  return Math.max(0, Math.min(100, score));
}

/**
 * Convert a health score to a letter grade.
 */
export function healthGrade(score: number): 'A' | 'B' | 'C' | 'D' | 'F' {
  if (score >= 90) return 'A';
  if (score >= 80) return 'B';
  if (score >= 70) return 'C';
  if (score >= 60) return 'D';
  return 'F';
}

/**
 * Determine the health trend from a history of scores.
 */
export function healthTrend(history: readonly number[]): 'improving' | 'declining' | 'stable' {
  if (history.length < 3) return 'stable';
  const recentCount = Math.min(3, Math.floor(history.length / 2));
  const older = history.slice(0, recentCount);
  const newer = history.slice(history.length - recentCount);
  const olderAvg = older.reduce((s, v) => s + v, 0) / older.length;
  const newerAvg = newer.reduce((s, v) => s + v, 0) / newer.length;

  if (newerAvg > olderAvg + 5) return 'improving';
  if (newerAvg < olderAvg - 5) return 'declining';
  return 'stable';
}

/**
 * Compute a summary of all metrics.
 */
export function computeMetricsSummary(metrics: CodeMetrics): string {
  const parts: string[] = [];
  parts.push(`Cyclomatic complexity: ${metrics.complexity.cyclomatic}`);
  parts.push(`Cognitive complexity: ${metrics.complexity.cognitive}`);
  parts.push(`Halstead volume: ${metrics.complexity.halstead.volume}`);
  parts.push(`Maintainability index: ${metrics.maintainability.index.toFixed(1)}`);
  parts.push(`Lines of code: ${metrics.maintainability.linesOfCode}`);
  parts.push(`Comment ratio: ${(metrics.maintainability.commentRatio * 100).toFixed(1)}%`);
  parts.push(`Duplication: ${(metrics.duplication.percentage * 100).toFixed(1)}%`);
  parts.push(`Duplicate blocks: ${metrics.duplication.blocks.length}`);
  return parts.join('\n');
}

/**
 * Generate a comprehensive text report from findings, metrics, and score.
 */
export function generateReport(
  findings: readonly AnalysisFinding[],
  metrics: CodeMetrics,
  score: number,
): string {
  const lines: string[] = [];

  lines.push('=== Code Analysis Report ===');
  lines.push('');
  lines.push(`Health Score: ${score}/100 (Grade: ${healthGrade(score)})`);
  lines.push('');

  lines.push('--- Metrics ---');
  lines.push(computeMetricsSummary(metrics));
  lines.push('');

  lines.push('--- Findings ---');
  lines.push(`Total: ${findings.length}`);
  lines.push(`  Critical: ${findings.filter((f) => f.severity === 'critical').length}`);
  lines.push(`  High: ${findings.filter((f) => f.severity === 'high').length}`);
  lines.push(`  Medium: ${findings.filter((f) => f.severity === 'medium').length}`);
  lines.push(`  Low: ${findings.filter((f) => f.severity === 'low').length}`);
  lines.push(`  Info: ${findings.filter((f) => f.severity === 'info').length}`);
  lines.push('');

  const prioritized = prioritizeFindings(findings);
  for (const finding of prioritized.slice(0, 10)) {
    lines.push(`[${finding.severity.toUpperCase()}] ${finding.title}`);
    lines.push(`  ${finding.description}`);
    lines.push(`  Line ${finding.location.start.line}:${finding.location.start.column}`);
    lines.push('');
  }

  if (prioritized.length > 10) {
    lines.push(`... and ${prioritized.length - 10} more findings`);
  }

  lines.push('=== End Report ===');
  return lines.join('\n');
}
